import { addLogErrorListener, type AppError } from '@engage-web/error';
import { posthog } from 'posthog-js/dist/module.full';
import { computed, ComputedRef, ref, unref } from 'vue';
import type { CaptureEvent, CaptureProperties, IdentifyUserOptions } from './models.d';

// initialised state of posthog
let intialised = false;

// called once to initialise the product
export function configureProduct(options: {
  token: string;
  host: string;
  anonymous?: boolean;
  worker?: boolean;
}): void {
  const { token, host, anonymous, worker } = options;

  posthog.init(token, {
    api_host: host,
    person_profiles: anonymous ? 'always' : 'identified_only',
    autocapture: false, // we don't want to track every button, a, input click/change etc.
    capture_pageview: false, // we are an SPA
    capture_pageleave: false,
    disable_session_recording: worker ?? false,
    disable_surveys: worker ?? false,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true, // mask password inputs
      },
      captureCanvas: {
        recordCanvas: true,
        canvasFps: 0.5,
        canvasQuality: '0.2',
      },
    },
  });

  // report all errors to posthog
  addLogErrorListener((error) => captureError(error));

  intialised = true;
}

// should be called when user logs out
export function resetProduct(): void {
  posthog.reset();
}

// should be called once on login/session start
export function identifyUser(options: IdentifyUserOptions): void {
  const { userId, email, displayName, firstName, lastName } = options;

  posthog.identify(userId, {
    email,
    name: displayName, // posthog uses `name` in it's ui
    first_name: firstName,
    last_name: lastName,
  });
}

// can be used to update custom properties, prefer using snake_case for keys
export function setPersonProperties(properties: Record<string, string | number | boolean>): void {
  posthog.setPersonProperties(properties);
}

// should be called when org context is changed
export function identifyOrg(options?: {
  orgId: string;
  orgName: string;
  deletedDate?: string;
}): void {
  if (!options) {
    posthog.resetGroups();
    return;
  }

  const { orgId, orgName, deletedDate } = options;

  posthog.group('org', orgId, {
    name: orgName,
    ...(deletedDate ? { deleted_date: deletedDate } : {}),
  });
}

export function identifyOrgId(orgId?: string): void {
  if (!orgId) {
    posthog.resetGroups();
    return;
  }

  posthog.group('org', orgId);
}

// track page changes
let lastPageView: number | undefined;

export function pageView(path: string): void {
  lastPageView = Date.now();

  posthog.capture('$pageview', { path });
}

export function pageLeave(path: string, nextPath: string): void {
  if (document.hidden || lastPageView === undefined) {
    return;
  }

  const leaveTime = Date.now();

  posthog.capture('$pageleave', {
    path,
    time_spent: leaveTime - lastPageView,
    next_path: nextPath,
    exited_at: leaveTime,
  });
}

export function captureEvent<K extends keyof CaptureEvent>(
  event: K,
  properties: CaptureEvent[K] & CaptureProperties,
): void {
  posthog.capture(event, properties);
}

export function captureError(error: AppError): void {
  posthog.captureException(error, {
    // $exception_fingerprint is used to group errors in posthog
    $exception_fingerprint: error.code,
    error_code: error.code,
    error_type: error.type,
    error_data: error.data,
  });
}

export function featureEnabled(
  feature: string,
  options?: {
    defaultValue?: boolean;
    localValue?: boolean;
  },
): ComputedRef<boolean | undefined> {
  const enabled = ref();

  if (!intialised) {
    enabled.value = options?.localValue ?? options?.defaultValue;
  } else {
    enabled.value = posthog.isFeatureEnabled(feature) ?? options?.defaultValue;
  }

  posthog.onFeatureFlags(() => {
    enabled.value = posthog.isFeatureEnabled(feature);
  });

  return computed(() => unref(enabled));
}

export function featureExperiment(feature: string) {
  const variant = ref<boolean | string>();
  const payload = ref<unknown>();

  posthog.onFeatureFlags(() => {
    variant.value = posthog.getFeatureFlag(feature);
    payload.value = posthog.getFeatureFlagPayload(feature);
  });

  return {
    variant: computed(() => unref(variant)),
    payload: computed(() => unref(payload)),
  };
}
